import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  notification,
  Skeleton,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { AppEnum } from '@enums/app.enum';
import { PopupButton, useCalendlyEventListener } from 'react-calendly';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import invariant from 'tiny-invariant';
import { getCountriesSelectOptions } from '@enums/country.enum';
import { EmailSourceEnum, EmailTemplateDisplayEnum } from '@enums/email.enum';

const { TextArea } = Input;

interface FormValues {
  companyAverageTicket: string;
  companyGrossRevenue: string;
  companyName: string;
  companyOperationCountries: string;
  companySector: string;
  companyTaxResidences: string;
  companyWebsite: string;
  email: string;
  fullName: string;
  message: string;
  phone: string;
}

export const ContactSalesSection = () => {
  const { t } = useTranslation();

  const [formSubmitted, setFormSubmitted] = useState(false);

  const [loading, setLoading] = useState(false);

  const [formInfo, setFormInfo] = useState<FormValues | null>(null);

  const [api, contextHolder] = notification.useNotification();

  const [form] = Form.useForm();

  const optionRevenue = [
    {
      label: 'under $1.000.000',
      value: 'under $1.000.000',
    },
    {
      label: '$1.000.000 - $5.000.000',
      value: '$1.000.000 - $5.000.000',
    },
    {
      label: '$5.000.000 - $10.000.000',
      value: '$5.000.000 - $10.000.000',
    },
    {
      label: 'over $10.000.000',
      value: 'over $10.000.000',
    },
  ];

  useCalendlyEventListener({
    onEventScheduled: () => {
      form.resetFields();

      setFormInfo(null);

      setFormSubmitted(!formSubmitted);

      api.success({
        description: t('contact.bookCallNotification'),
        duration: 4,
        message: t('contact.bookCallNotificationTitle'),
      });
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const buildPrefillAnswers = () => {
    invariant(formInfo, 'form info is required');

    return {
      customAnswers: {
        a1: formInfo.phone,
        a2: formInfo.companyName,
        a3: formInfo.companyWebsite,
        a4: formInfo.companyTaxResidences,
        a5: formInfo.companySector,
        a6: formInfo.companyOperationCountries,
        a7: formInfo.companyAverageTicket,
        a8: formInfo.companyGrossRevenue,
        a9: formInfo.message,
      },
      email: formInfo.email,
      name: formInfo.fullName,
    };
  };

  const onFinish = (formValues: FormValues) => {
    setLoading(true);

    emailjs
      .send(
        AppEnum.emailServiceId,
        AppEnum.emailTemplateId,
        {
          ...formValues,
          ar_display: EmailTemplateDisplayEnum.None,
          contact_display: EmailTemplateDisplayEnum.TableRow,
          details: '',
          source: EmailSourceEnum.ContactSales,
        },
        AppEnum.emailPublicKey
      )
      .then(
        () => {
          setLoading(false);

          setFormInfo(formValues);

          api.success({
            description: t('contact.contactUsNotification'),
            duration: 4,
            message: t('contact.contactUsNotificationTitle'),
            placement: 'bottomRight',
          });

          setFormSubmitted(!formSubmitted);
        },
        () => {
          setLoading(false);

          api.error({
            description: t('contact.contactUsError'),
            duration: 4,
            message: t('contact.contactUsErrorTitle'),
          });
        }
      );
  };

  return (
    <>
      {contextHolder}
      <Skeleton loading={loading} active>
        <section
          id="contact"
          className="w-full md:bg-bg-contact bg-no-repeat lg:bg-[length:100%] sm:bg-[position:0px] lg:bg-[position:180px]  bg-[#002C5F] text-white px-4 py-4 lg:px-32 scroll-mt-[200px] sm:scroll-mt-[330px] lg:scroll-mt-[120px]"
        >
          <Row gutter={[32, 32]} className="max-w-[1200px] ">
            <Col xs={24} lg={12}>
              <h2 className="text-[28px] lg:text-[40px] font-bold leading-tight font-archivo">
                {t('contact.title')} <br /> {t('contact.subTitle')}
              </h2>
              <p className="mt-4 text-base lg:text-lg text-white font-archivo">
                <strong className="font-semibold">{t('contact.expand')}</strong>
                <br /> {t('contact.unlock')}
              </p>
            </Col>
            <Col xs={24} lg={12}>
              <Card className="rounded-xl  bg-[#002C5F] shadow-none border-none">
                <Form form={form} onFinish={onFinish} layout="vertical">
                  <fieldset
                    disabled={formSubmitted}
                    className={
                      formSubmitted
                        ? 'opacity-60 pointer-events-none [&_input]:text-gray-400 [&_textarea]:text-gray-400'
                        : undefined
                    }
                  >
                    <Row gutter={16} className="h-[20%]">
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="fullName"
                          rules={[
                            {
                              message: t('error.contactForm.name'),
                              required: true,
                            },
                          ]}
                        >
                          <Input
                            placeholder={t('contact.formName')}
                            size="large"
                            className="rounded"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="email"
                          rules={[
                            {
                              message: t('error.contactForm.email'),
                              required: true,
                            },
                            {
                              type: 'email',
                            },
                          ]}
                        >
                          <Input
                            placeholder={t('contact.formEmail')}
                            size="large"
                            className="rounded"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="phone"
                          rules={[
                            {
                              message: t('error.contactForm.phone'),
                              required: true,
                            },
                          ]}
                        >
                          <Input
                            placeholder={t('contact.formPhone')}
                            size="large"
                            className="rounded"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="companyName"
                          rules={[
                            {
                              message: t('error.contactForm.company'),
                              required: true,
                            },
                          ]}
                        >
                          <Input
                            placeholder={t('contact.formCompany')}
                            size="large"
                            className="rounded"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="companyWebsite"
                          rules={[
                            {
                              message: t('error.contactForm.companyWebsite'),
                              required: true,
                              type: 'url',
                            },
                          ]}
                        >
                          <Input
                            placeholder={t('contact.formCompanyWebsite')}
                            size="large"
                            className="rounded"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="companyTaxResidences"
                          rules={[
                            {
                              message: t('error.contactForm.country'),
                              required: true,
                            },
                          ]}
                        >
                          <Select
                            className="rounded"
                            placeholder={t('contact.formTaxResidences')}
                            showSearch
                            size="large"
                            mode="multiple"
                            optionFilterProp="label"
                            options={getCountriesSelectOptions()}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="companySector"
                          rules={[
                            {
                              message: t('error.contactForm.companySector'),
                              required: true,
                            },
                          ]}
                        >
                          <Input
                            placeholder={t('contact.formCompanySector')}
                            size="large"
                            className="rounded"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="companyOperationCountries"
                          rules={[
                            {
                              message: t('error.contactForm.operationCountry'),
                              required: true,
                            },
                          ]}
                        >
                          <Select
                            className="rounded"
                            placeholder={t('contact.formOperationCountry')}
                            showSearch
                            size="large"
                            mode="multiple"
                            optionFilterProp="label"
                            options={getCountriesSelectOptions()}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="companyAverageTicket"
                          rules={[
                            {
                              message: t('error.contactForm.averageTicket'),
                              required: true,
                            },
                          ]}
                        >
                          <Input
                            placeholder={t('contact.formAverageTicket')}
                            size="large"
                            className="rounded"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="companyGrossRevenue"
                          rules={[
                            {
                              message: t('error.contactForm.revenue'),
                              required: true,
                            },
                          ]}
                        >
                          <Select
                            className="rounded"
                            placeholder={t('contact.formRevenue')}
                            showSearch
                            size="large"
                            optionFilterProp="label"
                            options={optionRevenue}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="message">
                      <TextArea
                        rows={4}
                        placeholder={t('contact.formMessage')}
                        className="rounded"
                      />
                    </Form.Item>
                    {!formSubmitted && (
                      <Form.Item>
                        <Button
                          type="primary"
                          size="large"
                          htmlType="submit"
                          className="bg-[#005FCC] border-none rounded-full w-full font-semibold font-archivo"
                        >
                          {t('common.contact')}
                        </Button>
                      </Form.Item>
                    )}
                  </fieldset>
                  {formSubmitted && (
                    <motion.div
                      initial={{ opacity: 0, x: 0 }}
                      animate={{
                        opacity: 1,
                        x: [0, -10, 10, -10, 10, -5, 5, 0],
                      }}
                      transition={{
                        duration: 0.6,
                        ease: 'easeInOut',
                        repeat: Infinity,
                        repeatDelay: 3,
                      }}
                    >
                      <PopupButton
                        url={AppEnum.calendlyUrl}
                        rootElement={document.body}
                        prefill={{ ...buildPrefillAnswers() }}
                        text={t('contact.bookCall')}
                        className="bg-[#005FCC] text-white font-semibold w-full rounded-full py-2 text-center block"
                      />
                    </motion.div>
                  )}
                </Form>
              </Card>
            </Col>
          </Row>
        </section>
      </Skeleton>
    </>
  );
};
